import React from "react";
import { formatMoney, getPayoutMethodLabel } from "./utils";

export default function WithdrawalPanel({
  earnings,
  withdrawalForm,
  updateWithdrawalField,
  savingWithdrawal,
  handleCreateWithdrawal,
  legacyWithdrawalAvailability,
  ibanWithdrawalTypeAvailability,
  ibanWithdrawalEnabled,
  supportedLegacyMethodOptions,
  activeLegacyMethods,
  activeLegacyMethodLabels,
  ibanPlatformFee,
  ibanNetAmount,
  selectedBankAccount,
  isAr,
}) {
  const { summary, bankAccounts, methods, ibanSettings } = earnings;

  return (
    <div
      className="card border-0 shadow-sm mb-4 provider-center__panel-card pc-panel--withdrawal"
      id="withdrawal-panel"
    >
      <div className="card-body">
        <div className="pc-section-head mb-3">
          <div className="pc-section-head__left">
            <span className="pc-icon-box pc-icon-box--blue"><i className="bi bi-send-check" /></span>
            <h5 className="mb-0">{isAr ? "طلب سحب جديد" : "New Withdrawal Request"}</h5>
          </div>
          <span className="badge text-bg-light">
            {formatMoney(summary.available_balance, summary.currency)}
          </span>
        </div>

        <div className="alert alert-light border mb-3">
          <div className="fw-semibold mb-1">
            {isAr ? "اختر مسار السحب المناسب" : "Choose the withdrawal path"}
          </div>
          <div className="small text-muted mb-1">
            {isAr
              ? "Stripe وPayPal: سيظهر هنا فقط الحساب الشخصي الحقيقي الذي تم ربطه بالفعل."
              : "Stripe and PayPal: only a real personal account that has already been linked will appear here."}
          </div>
          <div className="small text-muted">
            {isAr
              ? "سحب IBAN: استخدم حسابًا بنكيًا محفوظًا عبر IBAN."
              : "IBAN withdrawal: use one of your saved bank accounts via IBAN."}
          </div>
          <div className="small text-muted mt-1">
            {isAr
              ? `الأرباح الجديدة تُضاف إلى رصيدك، لكن لا تصبح متاحة للسحب إلا بعد ${summary.withdrawal_hold_days || 7} أيام للمراجعة.`
              : `New earnings are added to your balance, but become withdrawable only after ${summary.withdrawal_hold_days || 7} days for review.`}
          </div>
        </div>

        <form onSubmit={handleCreateWithdrawal}>
          <div className="row g-3">
            <div className="col-12 col-md-4">
              <label className="form-label">{isAr ? "نوع السحب" : "Withdrawal Type"}</label>
              <select
                className="form-select"
                value={withdrawalForm.methodType}
                onChange={(event) => updateWithdrawalField("methodType", event.target.value)}
              >
                {legacyWithdrawalAvailability?.supported ? (
                  <option value="legacy">
                    {isAr ? "طرق PayPal / Stripe المرتبطة" : "Linked PayPal / Stripe methods"}
                  </option>
                ) : null}
                {ibanWithdrawalTypeAvailability?.supported ? (
                  <option value="iban_wise">{getPayoutMethodLabel("iban_wise", isAr)}</option>
                ) : null}
              </select>
              <div className="form-text">
                {withdrawalForm.methodType === "legacy" && !legacyWithdrawalAvailability?.supported
                  ? legacyWithdrawalAvailability?.reason
                  : withdrawalForm.methodType === "iban_wise" &&
                      !ibanWithdrawalTypeAvailability?.supported
                    ? ibanWithdrawalTypeAvailability?.reason
                    : activeLegacyMethodLabels.length > 0
                      ? isAr
                        ? `الطرق النشطة حاليًا: ${activeLegacyMethodLabels.join("، ")}`
                        : `Active legacy methods: ${activeLegacyMethodLabels.join(", ")}`
                      : isAr
                        ? "لن يظهر هنا إلا حساب PayPal أو Stripe الشخصي المرتبط فعليًا."
                        : "Only a linked personal PayPal or Stripe account appears here."}
              </div>
            </div>

            <div className="col-12 col-md-4">
              {withdrawalForm.methodType === "iban_wise" ? (
                <>
                  <label className="form-label">{isAr ? "الحساب البنكي" : "Bank Account"}</label>
                  <select
                    className="form-select"
                    value={withdrawalForm.bankAccountId}
                    onChange={(event) => updateWithdrawalField("bankAccountId", event.target.value)}
                  >
                    <option value="">
                      {isAr ? "اختر حسابًا بنكيًا" : "Select a bank account"}
                    </option>
                    {bankAccounts
                      .filter((account) => account.status === "active")
                      .map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.bankName || account.fullName} - {account.iban}
                        </option>
                      ))}
                  </select>
                </>
              ) : (
                <>
                  <label className="form-label">{isAr ? "طريقة السحب" : "Payout Method"}</label>
                  <select
                    className="form-select"
                    value={withdrawalForm.payoutMethodId}
                    onChange={(event) => updateWithdrawalField("payoutMethodId", event.target.value)}
                  >
                    <option value="">{isAr ? "اختر الطريقة" : "Select a method"}</option>
                    {methods
                      .filter(
                        (method) =>
                          method.status === "active" &&
                          supportedLegacyMethodOptions.some((opt) => opt.code === method.method),
                      )
                      .map((method) => (
                        <option key={method.id} value={method.id}>
                          {getPayoutMethodLabel(method.method, isAr)}{" "}
                          {method.accountLabel ? `- ${method.accountLabel}` : ""}
                        </option>
                      ))}
                  </select>
                </>
              )}
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label">{isAr ? "المبلغ" : "Amount"}</label>
              <input
                type="number"
                min="1"
                step="0.01"
                className="form-control"
                value={withdrawalForm.amount}
                onChange={(event) => updateWithdrawalField("amount", event.target.value)}
              />
            </div>

            <div className="col-12">
              <label className="form-label">{isAr ? "ملاحظات" : "Notes"}</label>
              <input
                className="form-control"
                value={withdrawalForm.notes}
                onChange={(event) => updateWithdrawalField("notes", event.target.value)}
              />
            </div>
          </div>

          {withdrawalForm.methodType === "iban_wise" ? (
            <div className="mt-3">
              {!ibanWithdrawalEnabled ? (
                <div className="alert alert-warning mb-0">
                  {ibanSettings?.blockedReason ||
                    (isAr
                      ? "سحب IBAN معطّل حاليًا لهذا الحساب."
                      : "IBAN withdrawals are currently disabled for this account.")}
                </div>
              ) : bankAccounts.length === 0 ? (
                <div className="alert alert-warning mb-0">
                  {isAr
                    ? "أضف حسابًا بنكيًا واحدًا على الأقل لاستخدام السحب عبر IBAN."
                    : "Add at least one bank account to use IBAN withdrawals."}
                </div>
              ) : (
                <div className="border rounded-4 p-3 bg-light-subtle">
                  <div className="row g-3">
                    <div className="col-12 col-md-4">
                      <div className="small text-muted">{isAr ? "رسوم المنصة" : "Platform fee"}</div>
                      <div className="fw-semibold">
                        {formatMoney(ibanPlatformFee, summary.currency)}
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="small text-muted">
                        {isAr ? "الصافي المتوقع" : "Estimated net"}
                      </div>
                      <div className="fw-semibold">
                        {formatMoney(ibanNetAmount, summary.currency)}
                      </div>
                    </div>
                    <div className="col-12 col-md-4">
                      <div className="small text-muted">
                        {isAr ? "أول مراجعات يدوية" : "First manual reviews"}
                      </div>
                      <div className="fw-semibold">
                        {ibanSettings?.manualReviewThreshold ??
                          ibanSettings?.firstManualReviewCount ??
                          5}
                      </div>
                    </div>
                  </div>

                  {selectedBankAccount?.countryCode === "PS" ? (
                    <div className="alert alert-warning mt-3 mb-0">
                      {isAr
                        ? "السحب إلى البنوك الفلسطينية يحتاج مراجعة يدوية قبل الإرسال."
                        : "Withdrawals to Palestinian banks require manual review."}
                    </div>
                  ) : null}

                  <div className="small text-muted mt-3">
                    {isAr
                      ? "رسوم المنصة هنا هي عمولة IBAN الخاصة بالموقع فقط. أما أي رسوم من مزود التحويل مثل Wise أو رسوم بنك مراسل فقد تُفرض من جهة التحويل نفسها، وليست من عمولة الموقع."
                      : "The platform fee shown here is only the site's IBAN withdrawal fee. Any transfer-provider fees, such as Wise or correspondent-bank fees, may be charged by the payout provider itself and are not kept by the platform."}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <button
            className="btn btn-primary mt-3"
            type="submit"
            disabled={
              savingWithdrawal ||
              (withdrawalForm.methodType === "iban_wise"
                ? !ibanWithdrawalEnabled ||
                  !ibanWithdrawalTypeAvailability?.supported ||
                  !withdrawalForm.bankAccountId
                : !legacyWithdrawalAvailability?.supported ||
                  !withdrawalForm.payoutMethodId ||
                  activeLegacyMethods.length === 0)
            }
          >
            {savingWithdrawal
              ? isAr
                ? "جارٍ الإرسال..."
                : "Submitting..."
              : isAr
                ? "إرسال طلب السحب"
                : "Submit Withdrawal Request"}
          </button>
        </form>
      </div>
    </div>
  );
}
